import { __assign, __read, __rest } from "tslib";
import React, { useState, useRef } from "react";
import classNames from "classnames";
import { isChildOfType } from "../_util/is-child-of-type";
import { TabBar } from "./TabBar";
import { TabPanel } from "./TabPanel";
import { useConfig } from "../_util/config-context";
var getHiddenPanelStyle = function (vertical) { return ({
    height: 0,
    width: 0,
    overflow: "hidden",
    opacity: 0,
    padding: 0,
    margin: 0,
    pointerEvents: "none",
    display: "block",
    transform: vertical ? "translate3d(10px, 0, 0)" : "translate3d(0, 10px, 0)",
}); };
var animatedPanelStyle = {
    transition: "opacity .45s ease, transform .45s ease",
    transformOrigin: "center",
};
var defaultTabBarRender = function (c) { return React.createElement("a", null, c); };
export var Tabs = React.forwardRef(function Tabs(_a, ref) {
    var _b;
    var _c = _a.tabs, tabs = _c === void 0 ? [] : _c, _d = _a.destroyInactiveTabPanel, destroyInactiveTabPanel = _d === void 0 ? true : _d, activeId = _a.activeId, onActive = _a.onActive, defaultActiveId = _a.defaultActiveId, children = _a.children, _e = _a.animated, animated = _e === void 0 ? true : _e, placement = _a.placement, ceiling = _a.ceiling, addon = _a.addon, maxHeight = _a.maxHeight, className = _a.className, _f = _a.tabBarRender, tabBarRender = _f === void 0 ? defaultTabBarRender : _f, tabBarStyle = _a.tabBarStyle, _g = _a.activeTabAutoScrollIntoView, activeTabAutoScrollIntoView = _g === void 0 ? true : _g, _h = _a.disableTabScrolling, disableTabScrolling = _h === void 0 ? false : _h, props = __rest(_a, ["tabs", "destroyInactiveTabPanel", "activeId", "onActive", "defaultActiveId", "children", "animated", "placement", "ceiling", "addon", "maxHeight", "className", "tabBarRender", "tabBarStyle", "activeTabAutoScrollIntoView", "disableTabScrolling"]);
    var classPrefix = useConfig().classPrefix;
    // 如果都没有定义，获取第一个没被禁用的选项卡 id
    if (typeof activeId === "undefined" &&
        typeof defaultActiveId === "undefined") {
        var firstAvailable = tabs.find(function (x) { return !x.disabled; });
        // eslint-disable-next-line no-param-reassign
        defaultActiveId = firstAvailable ? firstAvailable.id : null;
    }
    // 注意 useState 是个 Hooks，不能写在 if 里面
    var _j = __read(useState(defaultActiveId), 2), internalActiveId = _j[0], setInternalActiveId = _j[1];
    // 非受控模式下，使用内部状态
    if (typeof activeId === "undefined") {
        // eslint-disable-next-line no-param-reassign
        activeId = internalActiveId;
        // eslint-disable-next-line no-param-reassign
        onActive = (function (onActive) { return function (tab, evt) {
            setInternalActiveId(tab.id);
            if (typeof onActive === "function") {
                onActive(tab, evt);
            }
        }; })(onActive);
    }
    var panelRenderedSetRef = useRef(new Set());
    function getTabPanels() {
        var panelRenderedSet = panelRenderedSetRef.current;
        var panelChildren = [];
        React.Children.forEach(children, function (child, index) {
            if (isChildOfType(child, TabPanel)) {
                var props_1 = child.props, key = child.key;
                var _a = props_1.style, style = _a === void 0 ? {} : _a, id = props_1.id;
                var active = activeId === props_1.id;
                if (active && !panelRenderedSet.has(activeId)) {
                    panelRenderedSet.add(activeId);
                }
                if (!active) {
                    Object.assign(style, getHiddenPanelStyle(placement === "left"));
                }
                if (animated) {
                    Object.assign(style, animatedPanelStyle);
                }
                if (child.props.forceRender ||
                    (destroyInactiveTabPanel && active) ||
                    (!destroyInactiveTabPanel && panelRenderedSet.has(props_1.id))) {
                    panelChildren.push(React.cloneElement(child, { key: key || id, style: style }));
                }
                else {
                    panelChildren.push(React.createElement(TabPanel, { id: id, key: key || id, style: style }));
                }
            }
            else if (React.isValidElement(child)) {
                panelChildren.push(React.cloneElement(child, { key: index }));
            }
            else {
                panelChildren.push(React.createElement("div", { key: index }, child));
            }
        });
        return panelChildren;
    }
    var containerClassName = classNames(classPrefix + "-tabs", (_b = {},
        // 垂直布局
        _b[classPrefix + "-tabs--vertical"] = placement === "left",
        _b[classPrefix + "-tabs--ceiling"] = ceiling,
        _b[className] = className,
        _b));
    return (React.createElement("div", __assign({ ref: ref, className: containerClassName }, props),
        React.createElement(TabBar, { tabs: tabs, activeId: activeId, onActive: onActive, disableTabScrolling: disableTabScrolling, addon: addon, vertical: placement === "left", maxHeight: maxHeight, tabBarRender: tabBarRender, style: tabBarStyle, activeTabAutoScrollIntoView: activeTabAutoScrollIntoView }),
        getTabPanels()));
});
Tabs.displayName = "Tabs";
//# sourceMappingURL=Tabs.js.map